import {
  Activity,
  Apple,
  Atom,
  Cherry,
  CircleDot,
  Eye,
  Flower,
  Flower2,
  Globe,
  Grape,
  HeartPulse,
  Link,
  Magnet,
  Microscope,
  RefreshCw,
  Scale,
  Shell,
  Sparkle,
  Sprout,
  Target,
  TreePalm,
  Brain,
  Clock,
  Dna,
  Droplet,
  FlaskConical,
  Heart,
  Leaf,
  Shield,
  Sparkles,
  Sun,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { EditorialIcon } from "@nh/shared";
import { cn } from "@/shared/lib/cn";

/**
 * El icono de un bloque editorial, resuelto por NOMBRE.
 *
 * ── Por qué el contrato manda un nombre y no un componente ───────────
 * `@nh/shared` lo comparte el servidor, y el servidor no puede importar
 * de `lucide-react`. Así que por el cable viaja `"bolt"` y la traducción
 * a un componente ocurre aquí, que es el único sitio donde existen los
 * dos lados.
 *
 * ── El `Record<EditorialIcon, …>` es la red ──────────────────────────
 * Si mañana alguien añade un nombre a `EDITORIAL_ICONS` y se olvida de
 * esta tabla, el error sale en `npm run typecheck` —hoy, aquí— y no en la
 * ficha de un cliente como un hueco sin explicación.
 *
 * ── Y un nombre desconocido NO tumba la ficha ────────────────────────
 * El esquema ya rechaza los inventados en las dos puertas (el API y el
 * modelo), así que llegar aquí con uno significa que la base trae algo
 * anterior a la lista cerrada. Ese caso devuelve `null`: se pierde el
 * icono, que es decoración, y se conserva el título, que es la
 * información. Reventar por un adorno sería peor que quedarse sin él.
 */
const EDITORIAL_ICON_COMPONENTS: Record<EditorialIcon, LucideIcon> = {
  bolt: Zap,
  dna: Dna,
  shield: Shield,
  leaf: Leaf,
  heart: Heart,
  brain: Brain,
  flask: FlaskConical,
  sparkles: Sparkles,
  clock: Clock,
  droplet: Droplet,
  sun: Sun,
  activity: Activity,
  atom: Atom,
  balance: Scale,
  berry: Cherry,
  cell: CircleDot,
  eye: Eye,
  flower: Flower2,
  globe: Globe,
  grape: Grape,
  leaves: Sprout,
  link: Link,
  magnet: Magnet,
  marigold: Flower,
  microscope: Microscope,
  palm: TreePalm,
  pomegranate: Apple,
  pulse: HeartPulse,
  refresh: RefreshCw,
  spark: Sparkle,
  spiral: Shell,
  target: Target,
};

export type ProductEditorialIconVariant =
  | "pillar"
  | "ingredient"
  | "ingredient-light"
  | "step"
  | "callout";

export interface ProductEditorialIconProps {
  /** El nombre que viene del catálogo. `undefined` = el bloque no lleva icono. */
  name: string | undefined;
  variant: ProductEditorialIconVariant;
}

/**
 * Las tres cápsulas del sistema, y por qué son distintas.
 *
 * `pillar` es un disco de tinta con el trazo en blanco: son cuatro
 * tarjetas claras en fila y el disco oscuro es lo que las ancla.
 * `ingredient` vive dentro de una tarjeta OSCURA, así que la cápsula es
 * un cuadrado apenas iluminado y el trazo va en lila —el violeta de marca
 * sobre casi negro se queda en 2.3:1 y deja de verse—.
 * `step` no lleva cápsula: en el proceso el peso visual ya lo carga el
 * número violeta de la izquierda, y dos círculos en la misma fila compiten.
 */
const wrapperClasses: Record<ProductEditorialIconVariant, string> = {
  pillar: "flex h-9 w-9 items-center justify-center rounded-ds-pill bg-ds-ink text-white",
  ingredient:
    "flex h-[38px] w-[38px] items-center justify-center rounded-[4px] border border-white/10 bg-white/[0.07] text-ds-lilac",
  /*
   * La misma caja de 38 px del ingrediente, en su versión clara: fondo
   * `mist`, borde `line` y el trazo en `violet-pop` — 5.96:1 sobre
   * blanco. Es literal del original, que pinta el icono en violet-pop en
   * los DOS temas; en el oscuro no se puede (2.78:1 sobre `ink`) y por
   * eso allí sube a `lilac`.
   */
  "ingredient-light":
    "flex h-[38px] w-[38px] items-center justify-center rounded-[4px] border border-ds-border bg-ds-mist text-ds-violet-pop",
  /*
   * `step` y `callout` NO ponen color: lo heredan de quien los monta.
   * El mismo trazo va en `ink-4` dentro de un paso del proceso, en
   * `violet-pop` dentro de un dato científico y en `lilac` sobre una
   * tarjeta oscura del comparativo — tres sitios con tres fondos, y un
   * color fijo aquí obligaría a un variante por fondo.
   */
  step: "inline-flex items-center justify-center",
  callout: "inline-flex items-center justify-center",
};

const glyphClasses: Record<ProductEditorialIconVariant, string> = {
  pillar: "h-4 w-4",
  ingredient: "h-[18px] w-[18px]",
  "ingredient-light": "h-[18px] w-[18px]",
  step: "h-[22px] w-[22px]",
  callout: "h-6 w-6",
};

export function ProductEditorialIcon({ name, variant }: ProductEditorialIconProps) {
  const Glyph = name ? EDITORIAL_ICON_COMPONENTS[name as EditorialIcon] : undefined;
  if (!Glyph) return null;

  return (
    <span className={cn("shrink-0", wrapperClasses[variant])}>
      <Glyph
        className={glyphClasses[variant]}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </span>
  );
}
